import React, { useState, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import SkillResource from '../../../common/resource/SkillResource';
import SkillAllAvailable from '../../../common/components/SkillAllAvailable';
import UserIcon from '../../../common/components/UserIcon';
import { LoadingAnimation } from '../../../common/components/Utilities';
import UserSkillResource from '../../../common/resource/UserSkillResource';

const groupUserSkills = (userSkills, currentUserId) => {
  const groupedSkills = userSkills.reduce((acc, userSkill) => {
    const existing = acc[userSkill.skill_id] || {
      skill: userSkill.skill,
      count: 0,
      users: [],
      self: false,
      selfUserSkillId: null,
    };
    const isSelf = userSkill.by_user_id === currentUserId;
    return {
      ...acc,
      [userSkill.skill_id]: {
        ...existing,
        count: existing.count + 1,
        users: [...existing.users, userSkill.by_user],
        self: existing.self || isSelf,
        selfUserSkillId: isSelf ? userSkill.id : existing.selfUserSkillId,
      },
    };
  }, {});

  return Object.values(groupedSkills).sort((left, right) => right.count - left.count);
};

function UserSkills({ user }) {
  const [loading, setLoading] = useState(true);
  const [availableLoading, setAvailableLoading] = useState(true);
  const [userSkills, setUserSkills] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [availableSkillsAfterLoad, setAvailableSkillsAfterLoad] = useState([]);
  const [filterValue, setFilterValue] = useState('');
  const groupedUserSkills = groupUserSkills(userSkills, window.user.id);

  const loadSkills = async () => {
    setLoading(true);
    const data = await UserSkillResource.index(user.id);
    setUserSkills(data);
    setLoading(false);
  };

  useEffect(() => { loadSkills(); }, [user]);
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const doLoadAvailableList = debounce(async () => {
    setAvailableLoading(true);
    const allSkills = await SkillResource.index();
    const assignedIds = new Set(userSkills.map((us) => us.skill.id));
    const response = allSkills.filter((skill) => !assignedIds.has(skill.id));
    setAvailableSkills(response);
    setAvailableSkillsAfterLoad(response);
    setAvailableLoading(false);
  }, 200);

  const endorse = async (event, skillId) => {
    if (event) {
      event.preventDefault();
    }

    setLoading(true);
    await UserSkillResource.post(user.id, skillId);
    await loadSkills();
    setLoading(false);
  };

  const unendorse = async (event, userSkillId) => {
    if (event) {
      event.preventDefault();
    }

    setLoading(true);
    await UserSkillResource.destroy(user.id, userSkillId);
    await loadSkills();
    doLoadAvailableList();
    setFilterValue('');
    setLoading(false);
  };

  return (
    <>
      <Card className="mb-4" style={{ minHeight: '130px' }}>
        <Card.Header>Skills</Card.Header>
        <LoadingAnimation loading={loading} />
        <table className="table">
          <tbody>
            {groupedUserSkills.map((item) => (
              <UserSkill
                user={user}
                key={item.skill.id}
                {...item}
                endorse={endorse}
                unendorse={unendorse}
              />
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="mb-4" style={{ overflow: 'inherit' }}>
        <Card.Header>{`Endorse ${user.first_name}`}</Card.Header>
        <Card.Body style={{ paddingTop: 0, paddingLeft: '1em', paddingRight: '1em' }}>
          <SkillAllAvailable
            user={user}
            doLoadAvailableList={doLoadAvailableList}
            loadSkills={loadSkills}
            availableSkills={availableSkills}
            setAvailableSkills={setAvailableSkills}
            availableLoading={availableLoading}
            setAvailableLoading={setAvailableLoading}
            availableSkillsAfterLoad={availableSkillsAfterLoad}
            setAvailableSkillsAfterLoad={setAvailableSkillsAfterLoad}
            filterValue={filterValue}
            setFilterValue={setFilterValue}
          />
        </Card.Body>
      </Card>
    </>
  );
}

function UserSkill({
  skill, count, users, self, selfUserSkillId, endorse, unendorse,
}) {
  let button = <button type="button" className="add-endorsement" title="Add your endorsement for this skill" onClick={(event) => endorse(event, skill.id)}>+</button>;

  if (self) {
    button = <button type="button" className="remove-endorsement" title="Remove your endorsement for this skill" onClick={(event) => unendorse(event, selfUserSkillId)}>Remove me</button>;
  }

  return (
    <tr>
      <td width={1} style={{ whiteSpace: 'nowrap' }}>
        <div className={`endorsement skill-${skill.type}`}>
          {skill.title}
          <span className="count">{count}</span>
        </div>
      </td>
      <td>
        {users.map((person, idx) => <UserIcon size="25px" className="mx-1" key={idx} user={person} round />)}
        {button}
      </td>
    </tr>
  );
}

export default UserSkills;
